/**
 * Estimates where a project stands from sparse information.
 *
 * A project consists of lists (phases) and those of tasks; both may or may
 * not have dates. Instead of counting tasks, progress and timeline are rolled
 * up per list: every value uses the most specific information available and
 * fills the gaps with an estimate, which is marked as such (`set` → `derived`
 * → `inferred`). The confidence and the one missing piece of information that
 * would help most are part of the result, so the UI can say how sure it is.
 *
 * Like the summary helpers a plain function, independent of React and i18n.
 */

export type DateSource = "set" | "derived" | "inferred";

export type PhaseStatus =
  | "done"
  | "ahead"
  | "onTrack"
  | "behind"
  | "notStarted"
  | "inProgress"
  | "empty";

export interface ProjectModelListInput {
  id: string;
  name: string;
  /** Dates set on the list itself (ms) */
  start?: number;
  end?: number;
  /** One entry per open task: its time effort in seconds, if estimated */
  openEfforts: (number | undefined)[];
  /** Due dates of open tasks (ms) */
  openDueDates: number[];
  /** Completion times of finished tasks (ms) */
  finishedAt: number[];
}

export interface ProjectModelInput {
  now: number;
  start?: number;
  end?: number;
  createdAt: number;
  /** In project order */
  lists: ProjectModelListInput[];
  /** Tasks without a list: count for progress, but form no phase */
  inbox?: ProjectModelListInput;
}

export interface PhaseModel {
  id: string;
  name: string;
  /** ms, or weight units when the model is relative */
  start: number;
  end: number;
  startSource: DateSource;
  endSource: DateSource;
  /** The less certain of both bounds */
  source: DateSource;
  weight: number;
  progress: number;
  /** Share that should be done by now, along the phase's own timeline */
  expected?: number;
  openCount: number;
  finishedCount: number;
  /** Counted with the average list size because it looks unplanned yet */
  padded: boolean;
  status: PhaseStatus;
}

export type ProjectModelMissing =
  | { kind: "projectEnd" }
  | { kind: "listEnd" | "listTasks"; listId: string; name: string };

export type TimelineSummaryKey =
  | "empty"
  | "done"
  | "relative"
  | "relativeNoTempo"
  | "late"
  | "onTime"
  | "behind"
  | "noForecast";

export interface ProjectModel {
  /** No end date anywhere: phases are laid out by size, not by calendar */
  relative: boolean;
  start: number;
  end: number;
  endSource?: DateSource;
  phases: PhaseModel[];
  totalWeight: number;
  doneWeight: number;
  progress: number;
  expected?: number;
  /** Finished weight units per week, from the last four weeks */
  unitsPerWeek?: number;
  remainingWeeks?: number;
  forecastEnd?: number;
  confidence: 1 | 2 | 3;
  missing?: ProjectModelMissing;
  summary: {
    key: TimelineSummaryKey;
    values: { count?: number; date?: number };
  };
}

const DAY = 24 * 60 * 60 * 1000;
const WEEK = 7 * DAY;
/** Lists with fewer tasks are probably not planned out yet */
const SPARSE_TASKS = 3;
/** Deviation from the expected progress that counts as ahead / behind */
const TOLERANCE = 0.15;

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));

const median = (xs: number[]): number | undefined => {
  if (xs.length === 0) return undefined;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

const SOURCE_RANK: Record<DateSource, number> = {
  set: 0,
  derived: 1,
  inferred: 2,
};
const worse = (a: DateSource, b: DateSource) =>
  SOURCE_RANK[a] >= SOURCE_RANK[b] ? a : b;
const SOURCE_SCORE: Record<DateSource, number> = {
  set: 1,
  derived: 0.6,
  inferred: 0,
};

interface Bounds {
  start?: number;
  end?: number;
  startSource?: DateSource;
  endSource?: DateSource;
}

export function getProjectModel(input: ProjectModelInput): ProjectModel {
  const { now } = input;
  const all = [...input.lists, ...(input.inbox ? [input.inbox] : [])];

  // ── Weights: estimated effort where known, else the median task ──────────
  const unit =
    median(
      all.flatMap((l) =>
        l.openEfforts.filter((e): e is number => !!e && e > 0),
      ),
    ) ?? 1;
  const taskWeight = (effort?: number) =>
    effort && effort > 0 ? effort / unit : 1;
  const openWeight = (l: ProjectModelListInput) =>
    l.openEfforts.reduce<number>((s, e) => s + taskWeight(e), 0);

  const sized = input.lists.map((l) => {
    const openW = openWeight(l);
    const doneW = l.finishedAt.length;
    return {
      l,
      openW,
      doneW,
      count: l.openEfforts.length + l.finishedAt.length,
    };
  });
  const planned = sized.filter((r) => r.count >= SPARSE_TASKS);
  const basis = planned.length ? planned : sized.filter((r) => r.count > 0);
  const avgListWeight = basis.length
    ? basis.reduce((s, r) => s + r.openW + r.doneW, 0) / basis.length
    : 1;

  const weighted = sized.map((r) => {
    const actual = r.openW + r.doneW;
    // Empty or thin lists with open work still count as a full phase,
    // otherwise an unplanned "Launch" makes the project look almost done
    const padded =
      r.count === 0 ||
      (r.count < SPARSE_TASKS && r.openW > 0 && actual < avgListWeight);
    return { ...r, weight: padded ? avgListWeight : actual, padded };
  });

  const inboxOpenW = input.inbox ? openWeight(input.inbox) : 0;
  const inboxDoneW = input.inbox ? input.inbox.finishedAt.length : 0;
  const inboxW = inboxOpenW + inboxDoneW;

  const totalWeight = weighted.reduce((s, r) => s + r.weight, 0) + inboxW;
  const doneWeight = weighted.reduce((s, r) => s + r.doneW, 0) + inboxDoneW;
  const progress = totalWeight > 0 ? doneWeight / totalWeight : 0;
  const remainingWeight = Math.max(0, totalWeight - doneWeight);

  // ── Tempo: finished tasks per week over the last four weeks ─────────────
  const finished = all
    .flatMap((l) => l.finishedAt)
    .filter((t) => t <= now)
    .sort((a, b) => a - b);
  let unitsPerWeek: number | undefined;
  if (finished.length >= 2) {
    const windowStart = Math.max(now - 4 * WEEK, finished[0]);
    const span = Math.max(WEEK, now - windowStart);
    const recent = finished.filter((t) => t >= windowStart).length;
    if (recent > 0) unitsPerWeek = recent / (span / WEEK);
  }
  const remainingWeeks =
    unitsPerWeek && remainingWeight > 0
      ? remainingWeight / unitsPerWeek
      : undefined;
  const forecastEnd =
    remainingWeeks !== undefined ? now + remainingWeeks * WEEK : undefined;

  // ── Dates per list: set on the list, else derived from its tasks ────────
  // Finished tasks show when work began, but only end a list that is done;
  // while tasks are open, only their due dates say when it should end
  const bounds: Bounds[] = weighted.map(({ l }) => {
    const b: Bounds = {};
    if (l.start !== undefined) [b.start, b.startSource] = [l.start, "set"];
    if (l.end !== undefined) [b.end, b.endSource] = [l.end, "set"];
    const isDone = l.openEfforts.length === 0 && l.finishedAt.length > 0;
    const startDates = [...l.finishedAt, ...l.openDueDates];
    const endDates = isDone ? l.finishedAt : l.openDueDates;
    if (b.end === undefined && endDates.length)
      [b.end, b.endSource] = [Math.max(...endDates), "derived"];
    if (b.start === undefined && startDates.length) {
      const min = Math.min(...startDates);
      if (b.end === undefined || min < b.end)
        [b.start, b.startSource] = [min, "derived"];
    }
    return b;
  });

  const knownStarts = bounds.flatMap((b) =>
    b.start !== undefined ? [b.start] : [],
  );
  // Only lists with work left say when the project ends
  const plannedEnds = bounds.flatMap((b, i) =>
    b.end !== undefined && weighted[i].openW > 0 ? [b.end] : [],
  );
  const start =
    input.start ??
    (knownStarts.length ? Math.min(...knownStarts) : input.createdAt);
  let end = input.end;
  let endSource: DateSource | undefined = end !== undefined ? "set" : undefined;
  if (end === undefined && plannedEnds.length) {
    end = Math.max(...plannedEnds);
    endSource = "derived";
  }
  const relative = end === undefined;

  // Duration for a phase that has no room left before the next known date
  const knownDurations = bounds.flatMap((b) =>
    b.start !== undefined && b.end !== undefined && b.end > b.start
      ? [b.end - b.start]
      : [],
  );
  const typicalDuration = median(knownDurations) ?? WEEK;

  // ── Fill the gaps: phases follow each other, split by their weight ──────
  if (relative) {
    let cursor = 0;
    weighted.forEach((r, i) => {
      bounds[i] = {
        start: cursor,
        end: cursor + r.weight,
        startSource: "inferred",
        endSource: "inferred",
      };
      cursor += r.weight;
    });
  } else {
    let cursor = start;
    for (let i = 0; i < bounds.length; i++) {
      const b = bounds[i];
      if (b.start === undefined)
        [b.start, b.startSource] = [cursor, "inferred"];
      if (b.end === undefined) {
        // The run of lists without an end, up to the next known date. A
        // derived start doesn't count: lists often run in parallel
        let anchor = end!;
        let runWeight = 0;
        for (let j = i; j < bounds.length; j++) {
          if (j > i && bounds[j].startSource === "set") {
            anchor = bounds[j].start!;
            break;
          }
          runWeight += weighted[j].weight;
          if (bounds[j].end !== undefined) {
            anchor = bounds[j].end!;
            break;
          }
        }
        const share = runWeight > 0 ? weighted[i].weight / runWeight : 1;
        const room = (anchor - b.start) * share;
        b.end = b.start + (room >= DAY ? room : typicalDuration);
        b.endSource = "inferred";
      }
      if (b.end! <= b.start!) b.end = b.start! + typicalDuration;
      cursor = b.end!;
    }
  }

  // ── Phases: progress against their own timeline ─────────────────────────
  const phases: PhaseModel[] = weighted.map((r, i) => {
    const b = bounds[i] as Required<Bounds>;
    const phaseProgress = r.weight > 0 ? clamp01(r.doneW / r.weight) : 0;
    const expected = relative
      ? undefined
      : clamp01((now - b.start) / (b.end - b.start));

    let status: PhaseStatus;
    if (r.count === 0) status = "empty";
    else if (r.openW === 0) status = "done";
    else if (relative) status = phaseProgress > 0 ? "inProgress" : "notStarted";
    else if (now < b.start) status = "notStarted";
    else if (phaseProgress < expected! - TOLERANCE) status = "behind";
    else if (phaseProgress > expected! + TOLERANCE) status = "ahead";
    else status = "onTrack";

    return {
      id: r.l.id,
      name: r.l.name,
      start: b.start,
      end: b.end,
      startSource: b.startSource,
      endSource: b.endSource,
      source: worse(b.startSource, b.endSource),
      weight: r.weight,
      progress: phaseProgress,
      expected,
      openCount: r.l.openEfforts.length,
      finishedCount: r.l.finishedAt.length,
      padded: r.padded,
      status,
    };
  });

  const projectEnd = relative ? totalWeight : end!;
  const expected =
    relative || totalWeight === 0
      ? undefined
      : (phases.reduce((s, p) => s + p.weight * (p.expected ?? 0), 0) +
          inboxW * clamp01((now - start) / Math.max(DAY, projectEnd - start))) /
        totalWeight;

  // ── How sure is this, and which single input would help most ────────────
  const dateScore = phases.length
    ? phases.reduce(
        (s, p) =>
          s + (SOURCE_SCORE[p.startSource] + SOURCE_SCORE[p.endSource]) / 2,
        0,
      ) / phases.length
    : 0;
  const confidence: 1 | 2 | 3 = relative
    ? 1
    : dateScore >= 0.75 && input.end !== undefined
      ? 3
      : dateScore >= 0.35
        ? 2
        : 1;

  let missing: ProjectModelMissing | undefined;
  const unresolved = phases.find(
    (p) => p.endSource === "inferred" && p.openCount + p.finishedCount > 0,
  );
  const empty = phases.find((p) => p.status === "empty");
  if (relative) missing = { kind: "projectEnd" };
  // The earliest guessed phase: every later one is chained to it
  else if (unresolved)
    missing = { kind: "listEnd", listId: unresolved.id, name: unresolved.name };
  else if (empty)
    missing = { kind: "listTasks", listId: empty.id, name: empty.name };
  else if (input.end === undefined) missing = { kind: "projectEnd" };

  // ── The sentence on top ─────────────────────────────────────────────────
  let summary: ProjectModel["summary"];
  if (totalWeight === 0 || (phases.length === 0 && inboxW === 0))
    summary = { key: "empty", values: {} };
  else if (remainingWeight === 0 && phases.every((p) => p.status !== "empty"))
    summary = { key: "done", values: {} };
  else if (relative)
    summary = remainingWeeks
      ? {
          key: "relative",
          values: { count: Math.max(1, Math.round(remainingWeeks)) },
        }
      : { key: "relativeNoTempo", values: {} };
  else if (forecastEnd !== undefined)
    summary =
      forecastEnd > end! + DAY
        ? {
            key: "late",
            values: {
              date: forecastEnd,
              count: Math.round((forecastEnd - end!) / DAY),
            },
          }
        : { key: "onTime", values: { date: forecastEnd } };
  else {
    const behind = phases.filter((p) => p.status === "behind").length;
    summary = behind
      ? { key: "behind", values: { count: behind } }
      : { key: "noForecast", values: {} };
  }

  return {
    relative,
    start: relative ? 0 : start,
    end: projectEnd,
    endSource,
    phases,
    totalWeight,
    doneWeight,
    progress,
    expected,
    unitsPerWeek,
    remainingWeeks,
    forecastEnd,
    confidence,
    missing,
    summary,
  };
}

/**
 * The time axis for drawing a model: project start to the latest of end,
 * phases, today and extra dates (milestones). A far-off forecast extends it
 * by at most half the project length, so it can't squash the chart.
 */
export function getTimelineDomain(
  model: ProjectModel,
  now: number,
  extraTimes: number[] = [],
) {
  const d0 = model.start;
  let d1 = Math.max(model.end, ...model.phases.map((p) => p.end));
  if (!model.relative) {
    d1 = Math.max(d1, now, ...extraTimes);
    if (model.forecastEnd)
      d1 = Math.max(
        d1,
        Math.min(model.forecastEnd, model.end + (model.end - d0) * 0.5),
      );
  }
  const span = Math.max(1, d1 - d0);
  const pos = (x: number) =>
    `${(Math.min(1, Math.max(0, (x - d0) / span)) * 100).toFixed(2)}%`;
  const len = (a: number, b: number) =>
    `${(Math.max(0, (Math.min(b, d1) - Math.max(a, d0)) / span) * 100).toFixed(2)}%`;
  return { d0, d1, pos, len };
}
